import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { StyledFormElementWrapper } from 'theme/styles';
import { IFormElementProps } from 'models/form';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StyledDatePicker } from 'components/molecules/FormDatePicker/styles';
import { colors } from 'theme/colors';
import { isValidDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { DateView } from '@mui/x-date-pickers';
import FormFooterLabel from '../../atoms/FormFooterLabel';

export enum EFormInputTypes {
  text = 'text',
  password = 'password',
}

interface IFormInput extends IFormElementProps {
  placeholder?: string;
  footerLabel?: string;
  minDate?: Date;
  openTo?: DateView;
  arrayName?: string;
  arrayIndex?: number;
  fieldName?: string;
  dateFormat?: string;
  disabled?: boolean;
  isClearable?: boolean;
}

export const FormDatePicker = ({
  name,
  label,
  footerLabel,
  control,
  errors,
  minDate,
  openTo = 'day',
  arrayName,
  arrayIndex,
  fieldName,
  placeholder,
  dateFormat,
  disabled = false,
  isClearable = false,
}: IFormInput) => {
  const { shortDateFormat, getDateFormat } = useGlobalProperty();

  useEffect(() => {
    if (!shortDateFormat) {
      getDateFormat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const error =
    arrayName && arrayIndex !== undefined && fieldName
      ? errors?.[arrayName]?.[arrayIndex]?.[fieldName]?.message
      : errors[name]?.message;

  const finalDateFormat = dateFormat || shortDateFormat;
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledFormElementWrapper disabled={disabled}>
        <FormLabel name={name} label={label} disabled={disabled} />
        <Controller
          name={name}
          control={control}
          defaultValue={null}
          render={({ field }) => (
            <StyledDatePicker
              openTo={openTo}
              onAccept={(newDate) => {
                if (newDate === null) {
                  field.onChange(null);
                }
              }}
              disabled={disabled}
              format={finalDateFormat}
              value={field.value !== '' && !!field.value ? new Date(field.value) : null}
              minDate={minDate}
              onChange={(e) => {
                if (isValidDate(e)) {
                  field.onChange(e);
                } else {
                  field.onChange('');
                }
              }}
              slotProps={{
                actionBar: {
                  actions: ['clear'],
                },
                field: {
                  clearable: isClearable,
                },
                textField: {
                  ref: field.ref,
                  sx: {
                    fieldset: {
                      borderRadius: 4,
                    },
                    input: {
                      fontSize: 14,
                      height: 32,
                      boxSizing: 'border-box',
                      ...(disabled
                        ? { backgroundColor: colors.gray100 }
                        : { backgroundColor: colors.white }),
                    },
                    'input::placeholder': {
                      fontSize: 14,
                      fontWeight: 400,
                      color: colors.gray300,
                      fontFamily: 'NotoSansRegular, sans-serif',
                      opacity: 1,
                    },
                    '& .MuiPickersOutlinedInput-notchedOutline': {
                      borderRadius: '4px',
                    },
                    '& .MuiPickersOutlinedInput-root': {
                      width: '100%',
                      height: '32px',
                      fontSize: '14px',
                      ...(disabled
                        ? { backgroundColor: colors.gray100 }
                        : { backgroundColor: colors.white }),
                      '& fieldset': {
                        borderColor: colors.gray300,
                      },
                      '&.Mui-error fieldset': {
                        borderColor: colors.error700,
                      },
                    },
                  },
                  slotProps: {
                    htmlInput: {
                      inputMode: 'numeric',
                      placeholder: placeholder || finalDateFormat.toUpperCase(),
                      id: name,
                      disabled: !!disabled,
                    },
                  },
                  error: !!error,
                },
              }}
            />
          )}
        />
        {footerLabel && <FormFooterLabel label={footerLabel} />}
        <FormErrorLabel label={error} />
      </StyledFormElementWrapper>
    </LocalizationProvider>
  );
};
